import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import api from "@/lib/api";
import {
  MessageSquare, Send, User, Users, ChevronRight,
  ArrowLeft, Clock
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function MessagesPage() {
  const { user } = useAuth();
  const [residents, setResidents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState("");
  const [sending, setSending] = useState(false);
  const [unreadByResident, setUnreadByResident] = useState({});

  const fetchResidents = useCallback(async () => {
    try {
      const r = await api.get("/residents");
      setResidents(r.data);
    } catch { /* ignore */ }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const params = selectedResident ? { resident_id: selectedResident.id } : {};
      const r = await api.get("/messages", { params });
      setMessages(r.data);
      // Calculate unread per resident
      if (!selectedResident) {
        const unread = {};
        r.data.forEach(m => {
          if (m.sender_type === "family" && !m.read_by_staff) {
            unread[m.resident_id] = (unread[m.resident_id] || 0) + 1;
          }
        });
        setUnreadByResident(unread);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [selectedResident]);

  useEffect(() => { fetchResidents(); }, [fetchResidents]);
  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleReply = async () => {
    if (!replyContent.trim() || !selectedResident) return;
    setSending(true);
    try {
      await api.post("/messages", { resident_id: selectedResident.id, content: replyContent });
      setReplyContent("");
      toast.success("Mensaje enviado");
      // Mark all family messages for this resident as read
      const familyMsgs = messages.filter(m => m.sender_type === "family" && !m.read_by_staff && m.resident_id === selectedResident.id);
      for (const m of familyMsgs) {
        await api.put(`/messages/${m.id}/read`);
      }
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar");
    }
    setSending(false);
  };

  const conversationMessages = selectedResident
    ? messages.filter(m => m.resident_id === selectedResident.id).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    : [];

  const totalUnread = Object.values(unreadByResident).reduce((a, b) => a + b, 0);

  // Residents with messages
  const residentsWithMsgs = residents.filter(r => {
    return messages.some(m => m.resident_id === r.id);
  });

  if (selectedResident) {
    return (
      <div className="space-y-4 animate-fade-in-up h-[calc(100vh-120px)] flex flex-col" data-testid="messages-conversation">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => { setSelectedResident(null); setLoading(true); fetchMessages(); }} className="rounded-full w-9 h-9" data-testid="back-to-inbox">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Button>
          <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold">
            {selectedResident.name?.charAt(0)}
          </div>
          <div>
            <h2 className="font-semibold text-slate-900 text-sm">{selectedResident.name}</h2>
            <p className="text-xs text-slate-400">Hab. {selectedResident.room_number || "S/N"}</p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 px-1">
          {conversationMessages.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-12">No hay mensajes para este residente</p>
          ) : (
            conversationMessages.map(msg => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_type === "staff" ? "justify-end" : "justify-start"}`}
                data-testid={`message-${msg.id}`}
              >
                <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                  msg.sender_type === "staff"
                    ? "bg-violet-600 text-white"
                    : "bg-slate-100 text-slate-900"
                }`}>
                  <p className={`text-[10px] font-semibold mb-1 ${msg.sender_type === "staff" ? "text-violet-200" : "text-slate-400"}`}>
                    {msg.sender_name}
                  </p>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  <p className={`text-[10px] mt-1.5 ${msg.sender_type === "staff" ? "text-violet-300" : "text-slate-400"}`}>
                    {new Date(msg.created_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Reply */}
        <div className="flex gap-3 items-end border-t border-slate-100 pt-4">
          <Textarea
            value={replyContent}
            onChange={e => setReplyContent(e.target.value)}
            placeholder="Escribir respuesta..."
            className="rounded-xl bg-slate-50 min-h-[60px] border-slate-200 flex-1 resize-none"
            data-testid="reply-input"
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
          />
          <Button
            onClick={handleReply}
            disabled={!replyContent.trim() || sending}
            className="rounded-xl h-10 px-5 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all"
            data-testid="send-reply-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="messages-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Mensajes</h1>
        <p className="text-sm text-slate-400 mt-0.5">
          Comunicación con familias
          {totalUnread > 0 && <span className="text-rose-500 font-medium ml-2">{totalUnread} sin leer</span>}
        </p>
      </div>

      {loading ? (
        <p className="text-slate-400 text-center py-12">Cargando mensajes...</p>
      ) : residentsWithMsgs.length === 0 && residents.length === 0 ? (
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-12 text-center">
            <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-sm text-slate-400 font-medium">No hay mensajes aún</p>
            <p className="text-xs text-slate-400 mt-1">Los familiares pueden enviar mensajes desde el Portal Familiar</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {residents.map(r => {
            const residentMsgs = messages.filter(m => m.resident_id === r.id);
            const unread = unreadByResident[r.id] || 0;
            const lastMsg = residentMsgs[0]; // already sorted desc from API
            if (residentMsgs.length === 0 && !unread) return null;
            return (
              <Card
                key={r.id}
                className="rounded-2xl border-slate-100 shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-[1px]"
                onClick={() => { setSelectedResident(r); setLoading(true); }}
                data-testid={`conversation-${r.id}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold flex-shrink-0">
                    {r.name?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 text-sm">{r.name}</h3>
                      {r.room_number && <span className="text-xs text-slate-400">Hab. {r.room_number}</span>}
                    </div>
                    {lastMsg && (
                      <p className="text-sm text-slate-500 truncate mt-0.5">
                        {lastMsg.sender_type === "staff" ? "Tú: " : `${lastMsg.sender_name}: `}
                        {lastMsg.content}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {unread > 0 && (
                      <Badge className="rounded-full bg-rose-500 text-white border-0 text-xs font-bold min-w-[22px] h-[22px] flex items-center justify-center">
                        {unread}
                      </Badge>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {/* Show residents without messages too */}
          {residents.filter(r => !messages.some(m => m.resident_id === r.id)).map(r => (
            <Card
              key={r.id}
              className="rounded-2xl border-slate-100 shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 opacity-50 hover:opacity-80"
              onClick={() => { setSelectedResident(r); setLoading(true); }}
              data-testid={`conversation-empty-${r.id}`}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 font-bold flex-shrink-0">
                  {r.name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-600 text-sm">{r.name}</h3>
                  <p className="text-xs text-slate-400">Sin mensajes</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-200" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
